import React from 'react';
import _ from 'lodash';

import {
  NetSuiteRecordLink,
  activityTemplates,
  useTabSearchParams
} from 'modules';
import {
  Form,
  LoadingOverlay,
  Table,
  fields,
  modalUtil
} from 'iron-forge-react-modules';
import {
  Box,
  Button,
  Grid,
  Paper,
  styled
} from '@mui/material';

import { useForm } from 'react-hook-form';
import * as EditCustomerModal from './EditCustomerModal/EditCustomerModal';
import * as dataService from './dataService';
import * as types from './types';



const TableComponent = Table.genTableComponent<types.IColumnId>();
const ControlledTextField = fields.TextField.genControllerComponent<types.IFilter>();
const FormContainerBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2)
}));



export const CustomersActivity: React.FC = () => {
  const [customerId, setCustomerId] = useTabSearchParams({
    paramName: 'customerId',
    defaultTabId: ''
  });
  const { modalType2TargetId, setModalIsOpen } = modalUtil.useModalTrackers(['editCustomer']);

  const form = useForm<types.IFilter>({
    defaultValues: {
      searchText: ''
    }
  });

  const {
    queryTuple,
    pageInfo,
    fnQuery,
    fnQueryPage
  } = dataService.useQuery();



  React.useEffect(() => {
    if (customerId !== modalType2TargetId.editCustomer) {
      setModalIsOpen('editCustomer', customerId ?? '');
    }
  }, [
    customerId,
    modalType2TargetId.editCustomer,
    setModalIsOpen
  ]);

  const rows = React.useMemo(
    () => _.map(queryTuple.data.customers, (customer): types.IRow => ({
      id: customer.id,
      popoverOptions: [
        {
          id: 'edit',
          label: "Edit"
        }
      ],
      columnId2Value: {
        lastName: {
          type: 'string',
          label: customer.lastName
        },
        firstName: {
          type: 'string',
          label: customer.firstName
        },
        email: {
          type: 'string',
          label: customer.email
        },
        phoneNumber: {
          type: 'string',
          label: customer.phoneNumber
        },
        netSuite: {
          type: 'component',
          sortableValue: customer.email,
          label: customer.recordUrl ? (
            <NetSuiteRecordLink.FC
              type="customer"
              urlToRecord={customer.recordUrl} />
          ) : <div />
        }
      }
    })),
    [queryTuple]
  );



  const processSubmit = React.useCallback(
    (filter: types.IFilter) => {
      fnQuery({ filter });
    },
    [fnQuery]
  );

  const handleClickRowPopoverOption = React.useCallback(
    (rowId: string, optionId: string) => {
      if (optionId === 'edit') {
        setCustomerId(rowId);
      }
    },
    [setCustomerId]
  );

  const handleSave = React.useCallback(
    () => {
      setCustomerId('');
      queryTuple.refetch?.();
    },
    [queryTuple, setCustomerId]
  );

  return (
    <>
      <activityTemplates.AdminActivityTemplate.FC>
        <Paper>
          <FormContainerBox>
            <Form.FC
              disableDefaultOverflow
              onSubmit={form.handleSubmit(processSubmit)}>
              <Grid container>
                <Grid
                  item
                  xs={6}
                  md={2}>
                  <ControlledTextField
                    fieldKey="searchText"
                    formControl={form.control}
                    label="Search" />
                </Grid>

                <Grid
                  item
                  xs={6}
                  md={2}>
                  <Button type="submit">
                    Search
                  </Button>
                </Grid>
              </Grid>
            </Form.FC>
          </FormContainerBox>

          <TableComponent
            rows={rows}
            paginationOptions={Table.pageInfo2PaginationOptions(pageInfo, fnQueryPage)}
            onClickRowPopoverOption={handleClickRowPopoverOption}
            initSortOptions={{
              columnId: 'lastName',
              direction: 'ascending'
            }}
            onChangeSortOptions={sort => fnQuery({ sort })}
            columns={[
              {
                id: 'lastName',
                label: "Last Name"
              },
              {
                id: 'firstName',
                label: "First Name"
              },
              {
                id: 'email',
                label: "Email"
              },
              {
                id: 'phoneNumber',
                label: "Phone Number"
              },
              {
                id: 'netSuite',
                label: "NetSuite"
              }
            ]} />
        </Paper>
      </activityTemplates.AdminActivityTemplate.FC>

      <LoadingOverlay.FC active={queryTuple.loading} />

      {modalType2TargetId.editCustomer && (
        <EditCustomerModal.FC
          customerId={modalType2TargetId.editCustomer}
          onClose={() => setCustomerId('')}
          onSave={handleSave} />
      )}
    </>
  );
};
